import React, { createContext, useContext, useReducer, useEffect } from "react";
import dummyData from "../dummy-backend.json";

// Initial state
const initialState = {
  loading: true,
  products: [],
  productDetails: {},
  cart: [],
  totalPrice: 0,
};

// Action types
export const ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_PRODUCTS: "SET_PRODUCTS",
  SET_PRODUCT_DETAILS: "SET_PRODUCT_DETAILS",
  ADD_TO_CART: "ADD_TO_CART",
  REMOVE_FROM_CART: "REMOVE_FROM_CART",
  UPDATE_QUANTITY: "UPDATE_QUANTITY",
  CLEAR_CART: "CLEAR_CART",
  CLEAR_PRODUCT_DETAILS: "CLEAR_PRODUCT_DETAILS",
  RESTORE_CART: "RESTORE_CART",
};

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ACTIONS.SET_PRODUCTS:
      return {
        ...state,
        loading: false,
        products: action.payload,
      };

    case ACTIONS.SET_PRODUCT_DETAILS:
      return {
        ...state,
        loading: false,
        productDetails: action.payload,
      };

    case ACTIONS.RESTORE_CART:
      return {
        ...state,
        cart: action.payload.cart || [],
        totalPrice: action.payload.totalPrice || 0,
        loading: false,
      };

    case ACTIONS.ADD_TO_CART:
      // Legacy context cart — prefer variant_id when present; sku-only is deprecated.
      const existingItem = state.cart.find((item) =>
        action.payload.variant_id != null
          ? item.variant_id === action.payload.variant_id &&
            String(item.pack_id ?? "") === String(action.payload.pack_id ?? "")
          : item.sku === action.payload.sku
      );

      if (existingItem) {
        const updatedCart = state.cart.map((item) =>
          (action.payload.variant_id != null
            ? item.variant_id === action.payload.variant_id &&
              String(item.pack_id ?? "") === String(action.payload.pack_id ?? "")
            : item.sku === action.payload.sku)
            ? { ...item, selected_quantity: action.payload.selected_quantity }
            : item
        );

        const newTotalPrice = updatedCart.reduce(
          (total, item) => total + (item.selling_price || 0) * (item.selected_quantity || 1),
          0
        );

        return {
          ...state,
          cart: updatedCart,
          totalPrice: newTotalPrice,
        };
      } else {
        const newCart = [...state.cart, action.payload];
        const newTotalPrice = newCart.reduce(
          (total, item) => total + (item.selling_price || 0) * (item.selected_quantity || 1),
          0
        );

        return {
          ...state,
          cart: newCart,
          totalPrice: newTotalPrice,
        };
      }

    case ACTIONS.REMOVE_FROM_CART:
      // payload may be variant_id or legacy sku string
      const filteredCart = state.cart.filter((item) =>
        action.payload != null && typeof action.payload === "object"
          ? !(
              item.variant_id === action.payload.variant_id &&
              String(item.pack_id ?? "") === String(action.payload.pack_id ?? "")
            )
          : item.sku !== action.payload && item.variant_id !== action.payload
      );
      const newTotalPrice = filteredCart.reduce(
        (total, item) => total + (item.selling_price || 0) * (item.selected_quantity || 1),
        0
      );

      return {
        ...state,
        cart: filteredCart,
        totalPrice: newTotalPrice,
      };

    case ACTIONS.UPDATE_QUANTITY:
      const { sku, quantity, variant_id, pack_id } = action.payload;
      const updatedCart = state.cart.map((item) => {
        const match =
          variant_id != null
            ? item.variant_id === variant_id &&
              String(item.pack_id ?? "") === String(pack_id ?? "")
            : item.sku === sku;
        return match ? { ...item, selected_quantity: quantity } : item;
      });

      const updatedTotalPrice = updatedCart.reduce(
        (total, item) => total + (item.selling_price || 0) * (item.selected_quantity || 1),
        0
      );

      return {
        ...state,
        cart: updatedCart,
        totalPrice: updatedTotalPrice,
      };

    case ACTIONS.CLEAR_CART:
      return {
        ...state,
        cart: [],
        totalPrice: 0,
      };

    case ACTIONS.CLEAR_PRODUCT_DETAILS:
      return {
        ...state,
        productDetails: {},
        loading: false,
      };

    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load cart from sessionStorage on mount
  useEffect(() => {
    const savedCart = sessionStorage.getItem("cart");
    const savedTotalPrice = sessionStorage.getItem("totalPrice");

    if (savedCart) {
      try {
        const cart = JSON.parse(savedCart);
        const totalPrice = parseFloat(savedTotalPrice) || 0;

        dispatch({ type: ACTIONS.RESTORE_CART, payload: { cart, totalPrice } });
      } catch (error) {
        console.error("Error restoring cart from sessionStorage:", error);
        // Clear corrupted data
        sessionStorage.removeItem("cart");
        sessionStorage.removeItem("totalPrice");
        // Set loading to false even if restoration fails
        dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      }
    } else {
      // No cart to restore, set loading to false
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, []);

  // Save cart to sessionStorage whenever cart changes
  useEffect(() => {
    if (state.cart.length > 0) {
      sessionStorage.setItem("cart", JSON.stringify(state.cart));
      sessionStorage.setItem("totalPrice", state.totalPrice.toString());
    } else {
      sessionStorage.removeItem("cart");
      sessionStorage.removeItem("totalPrice");
    }
  }, [state.cart, state.totalPrice]);

  // API functions
  const getProducts = async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });

    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      dispatch({
        type: ACTIONS.SET_PRODUCTS,
        payload: dummyData.products,
      });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const getProductDetails = async (id) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });

    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 300));

      const product = dummyData.products.find((p) => p.id === parseInt(id));

      if (!product) {
        throw new Error("Product not found");
      }

      dispatch({
        type: ACTIONS.SET_PRODUCT_DETAILS,
        payload: product,
      });
    } catch (error) {
      console.error("Failed to fetch product details:", error);
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const addToCart = (product) => {
    dispatch({ type: ACTIONS.ADD_TO_CART, payload: product });
  };

  const removeFromCart = (sku) => {
    dispatch({ type: ACTIONS.REMOVE_FROM_CART, payload: sku });
  };

  const updateQuantity = (sku, quantity, sellingPrice) => {
    dispatch({
      type: ACTIONS.UPDATE_QUANTITY,
      payload: { sku, quantity, sellingPrice },
    });
  };

  const clearCart = () => {
    dispatch({ type: ACTIONS.CLEAR_CART });
  };

  const clearProductDetails = () => {
    dispatch({ type: ACTIONS.CLEAR_PRODUCT_DETAILS });
  };

  const value = {
    ...state,
    getProducts,
    getProductDetails,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    clearProductDetails,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
